import React, { useState, useRef, useEffect } from 'react';
import { useStyles } from '../../../lib/hooks/useStyles';
import theme from '../../../lib/themes/linkpick/new_theme';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const countries = [
  { code: 'FR', dialCode: '+33', flag: 'https://flagcdn.com/w20/fr.png', name: 'France' },
  { code: 'US', dialCode: '+1', flag: 'https://flagcdn.com/w20/us.png', name: 'United States' },
  { code: 'GB', dialCode: '+44', flag: 'https://flagcdn.com/w20/gb.png', name: 'United Kingdom' },
  { code: 'DE', dialCode: '+49', flag: 'https://flagcdn.com/w20/de.png', name: 'Germany' },
  { code: 'ES', dialCode: '+34', flag: 'https://flagcdn.com/w20/es.png', name: 'Spain' },
  { code: 'IT', dialCode: '+39', flag: 'https://flagcdn.com/w20/it.png', name: 'Italy' },
  { code: 'BE', dialCode: '+32', flag: 'https://flagcdn.com/w20/be.png', name: 'Belgium' },
  { code: 'CH', dialCode: '+41', flag: 'https://flagcdn.com/w20/ch.png', name: 'Switzerland' },
  { code: 'CA', dialCode: '+1', flag: 'https://flagcdn.com/w20/ca.png', name: 'Canada' },
];

export const PhoneInput = ({ 
  onChange,
  onBlur,
  value = '',
  name,
  placeholder = '',
  defaultCountry = 'FR',
  error = false,
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find(c => c.code === defaultCountry) || countries[0]
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { classes, css } = useStyles(styles);
  const phoneInputRef = useRef(null);

  useEffect(() => {
    if (!isDropdownOpen) return;
    function handleClickOutside(event) {
      if (phoneInputRef.current && !phoneInputRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePhoneChange = (e) => {
    let phoneNumber = e.target.value;
    // Nettoyer le numéro (garder seulement les chiffres)
    let cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, '');
    // Limiter à 10 chiffres maximum
    cleanPhoneNumber = cleanPhoneNumber.slice(0, 10);
    // Créer le numéro complet pour la validation (sans espaces)
    const fullNumber = selectedCountry.dialCode + cleanPhoneNumber;
    onChange && onChange({
      target: {
        name,
        value: fullNumber
      }
    });
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    
    // Mettre à jour le numéro avec le nouveau code pays
    const currentPhone = value.replace(/^\+\d+/, '');
    const newFullNumber = country.dialCode + currentPhone;
    
    onChange && onChange({
      target: {
        name,
        value: newFullNumber
      }
    });
  };

  const getDisplayNumber = () => {
    if (!value) return '';
    
    // Extraire le numéro sans le code pays
    const phoneWithoutCountryCode = value.replace(selectedCountry.dialCode, '');
    
    // Formatage spécial pour la France
    if (selectedCountry.code === 'FR' && phoneWithoutCountryCode.length > 0) {
      const digits = phoneWithoutCountryCode.replace(/\D/g, '');
      if (digits.length <= 10) {
        return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
      }
    }
    
    return phoneWithoutCountryCode;
  };

  return (
    <div ref={phoneInputRef} className={css(
      theme.components.phoneInput.container,
      error && theme.components.phoneInput.error
    )}>
      {/* Sélecteur de pays */}
      <div 
        className={css(theme.components.phoneInput.countrySelector)}
        onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
      >
        <span className={css(theme.components.phoneInput.flag)}>
          <img 
            src={selectedCountry.flag} 
            alt={selectedCountry.name} 
            className={css(theme.components.phoneInput.flag)}
          />
        </span>
        <span className={css(theme.components.phoneInput.dialCode)}>
          {selectedCountry.dialCode}
        </span>
        <span className={css(theme.components.phoneInput.arrow)}>
          <KeyboardArrowDownIcon />
        </span>
        {/* Dropdown des pays */}
        {isDropdownOpen && !disabled && (
          <div className={css(theme.components.phoneInput.dropdown)}>
            {countries.map((country) => (
              <div
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                onMouseEnter={() => setHoveredCountry(country.code)}
                onMouseLeave={() => setHoveredCountry(null)}
                className={css(
                  theme.components.phoneInput.option,
                  (selectedCountry.code === country.code || hoveredCountry === country.code) && theme.components.phoneInput.optionSelected
                )}
              >
                <span className={css(theme.components.phoneInput.flag)}>
                  <img 
                    src={country.flag} 
                    alt={country.name} 
                    className={css(theme.components.phoneInput.flag)}
                  />
                </span>
                <span className={css(theme.components.phoneInput.dialCode)}>
                  {country.dialCode}
                </span>
                <span className={css(theme.components.phoneInput.countryName)}>
                  {country.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Input du numéro */}
      <input
        type="tel"
        value={getDisplayNumber()}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handlePhoneChange}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur && onBlur(e);
        }}
        className={css(
          theme.components.phoneInput.input,
          error && theme.components.phoneInput.inputError
        )}
      />
    </div>
  );
};

const styles = () => ({
  container: theme.components.textField.container,
  input: theme.components.textField.input
}); 